import { defineStore } from 'pinia'
import { apiGetMyPermissions } from '@/services/permissionMatrixService'

// Quyền per-page (ma trận 1) đọc thật từ BE: GET /api/v1/permissions/me
// Khi BE chưa sẵn sàng (load lỗi) -> isLoaded = false, các nơi dùng sẽ fallback về Permission.ts tĩnh.

export interface ResourcePerm {
  can_create: boolean
  can_read: boolean
  can_update: boolean
  can_delete: boolean
  can_import: boolean
  can_export: boolean
}

// Map tên action (như đang dùng ở usePermission) -> field trong ResourcePerm
const ACTION_FIELD: Record<string, keyof ResourcePerm> = {
  create: 'can_create',
  read: 'can_read',
  edit: 'can_update',
  update: 'can_update',
  reorder: 'can_update',
  delete: 'can_delete',
  import: 'can_import',
  export: 'can_export',
}

export const usePermissionStore = defineStore('permission', {
  state: () => ({
    permissions: {} as Record<string, ResourcePerm>,
    isAdmin: false,
    isLoaded: false, // đã nhận quyền từ BE thành công chưa
    loadFailed: false, // lần load gần nhất có lỗi không (BE chưa có / 401...)
  }),

  getters: {
    // Có dữ liệu quyền cho resource này không
    has:
      (state) =>
      (resourceCode: string): boolean =>
        Object.prototype.hasOwnProperty.call(state.permissions, resourceCode),
  },

  actions: {
    // Tải quyền của chính mình; nuốt lỗi (không vỡ app) để fallback static khi BE chưa có
    async loadMyPermissions() {
      try {
        const body = await apiGetMyPermissions()
        const data = body?.data ?? body
        const map: Record<string, ResourcePerm> = {}
        for (const p of data?.permissions ?? []) {
          map[p.resource_code] = {
            can_create: !!p.can_create,
            can_read: !!p.can_read,
            can_update: !!p.can_update,
            can_delete: !!p.can_delete,
            can_import: !!p.can_import,
            can_export: !!p.can_export,
          }
        }
        this.permissions = map
        this.isAdmin = !!data?.is_admin
        this.isLoaded = true
        this.loadFailed = false
      } catch (e) {
        // BE chưa có / lỗi -> giữ isLoaded = false để nơi dùng fallback static
        this.isLoaded = false
        this.loadFailed = true
      }
    },

    // Kiểm tra 1 hành động trên 1 resource. Trả undefined nếu không có dữ liệu (để caller fallback).
    can(resourceCode: string, action: string): boolean | undefined {
      if (!this.isLoaded) return undefined
      if (this.isAdmin) return true
      const perm = this.permissions[resourceCode]
      if (!perm) return undefined
      const field = ACTION_FIELD[action]
      if (!field) return undefined
      return perm[field]
    },

    canRead(resourceCode: string): boolean | undefined {
      return this.can(resourceCode, 'read')
    },

    reset() {
      this.permissions = {}
      this.isAdmin = false
      this.isLoaded = false
      this.loadFailed = false
    },
  },
})
